import cx from "classnames";

import { IAddress, ReviewRating } from "common/types";
import { OrderStatus as OrderStatusEnum } from "common/types/orders";
import { Optional } from "common/utils/types";
import { toDate } from "common/utils/dates";
import { toDollars } from "common/utils/formatting";

import Box from "client/common/components/layouts/Box";
import Button from "client/common/components/buttons/Button";
import { urls } from "client/routes";
import Banner from "client/common/components/content/Banner";
import { ModalStatus } from "client/common/types";
import Tooltip from "client/common/components/content/Tooltip";

import OrderStatus from "./OrderStatus";
import { ClientListOrder, ClientListOrderItem } from "../types";
import OrderItem from "./OrderItem";

import styles from "../styles/ListOrder.module.less";

type SummaryBarProps = {
  order: ClientListOrder;
};

const SummaryBar = ({ order }: SummaryBarProps) => {
  const getAddressItems = (address: Optional<IAddress>, title: string) => {
    if (!address) {
      return [];
    }

    return [
      {
        value: `${address.address_one} ${address.address_two}`,
        label: title,
        className: styles.address,
      },
      {
        value: `${address.city}, ${address.state}`,
        label: "City / State",
        className: styles.city,
      },
    ];
  };

  const summaryItems = [
    {
      value: order.display_number,
      label: "Order number",
      className: styles.number,
    },
    {
      value: toDate(order.order_placed_time),
      label: "Date placed",
      className: styles.placed,
    },
    {
      value: toDollars(order.total),
      label: "Total amount",
      className: styles.amount,
    },
    ...(order.has_any_shipping
      ? getAddressItems(order.shipping_address, "Shipping Address")
      : getAddressItems(order.pickup_address, "Pickup Address")),
  ];

  return (
    <div className={styles.summaryBar}>
      {summaryItems.map((info) => (
        <div className={cx(styles.info, info.className)} key={info.label}>
          <div className={styles.value}>{info.value}</div>
          <div className={styles.label}>{info.label}</div>
        </div>
      ))}
    </div>
  );
};

type ListOrderProps = {
  order: ClientListOrder;
  onOpenReview: (event: { orderItem: ClientListOrderItem; rating?: ReviewRating }) => void;
  onOpenModifications: () => void;
  onOpenCancelation: () => void;
};

const ListOrder = ({
  order,
  onOpenReview,
  onOpenModifications,
  onOpenCancelation,
}: ListOrderProps) => {
  return (
    <Box className={styles.order} key={order.id}>
      <div className={styles.header}>
        <SummaryBar order={order} />
        <h4 className={styles.title}>Order {order.display_number}</h4>
        <div className={styles.status}>
          <OrderStatus order={order} />
        </div>
      </div>
      {order.status === OrderStatusEnum.PaymentFailed && (
        <Banner inline={true} variant="danger">
          <Banner.Text
            label="Your current payment method on file has been declined."
            actions={[
              {
                children: "Update payment method",
                dest: urls.orders.detail({
                  params: { id: order.id },
                  search: { paymentModalIsVisible: ModalStatus.Open },
                }),
              },
            ]}
          />
        </Banner>
      )}
      <div>
        {order.order_items.map((orderItem) => (
          <div className={styles.orderItem} key={orderItem.item.id}>
            <OrderItem
              order={order}
              orderItem={orderItem}
              onOpenModifications={onOpenModifications}
              onOpenReview={(rating) => onOpenReview({ orderItem, rating })}
              classNames={{
                info: styles.info,
                related: styles.related,
                item: styles.item,
                content: styles.content,
                image: styles.image,
                timeline: styles.timeline,
              }}
            />
          </div>
        ))}
      </div>
      <div className={styles.actions}>
        {/* Hidden until we have a bulk add to cart */}
        {/* <Button className={styles.reorder} size='s' variant='secondary'>
                Reorder {pluralized(order.num_items, 'item')}
              </Button> */}
        <div className={styles.main}>
          {/* sc25305 - Hidden until we can print just the individual order here */}
          {/* <Button className={styles.print} size='s' variant='secondary'>
                Print Order
              </Button>  */}
          {order.status !== OrderStatusEnum.Canceled && (
            <Tooltip
              className={styles.button}
              isEnabled={true}
              maxWidth={400}
              content={
                order.is_cancelable
                  ? "Your cancelation window is limited. Please cancel now, if needed."
                  : "We're sorry — this order is already processing and cannot be canceled."
              }
            >
              <Button
                size="s"
                variant="secondary"
                isLoading={false}
                disabled={!order.is_cancelable}
                onClick={onOpenCancelation}
              >
                Cancel order
              </Button>
            </Tooltip>
          )}
          <div className={styles.button}>
            <Button size="s" dest={urls.orders.detail({ params: { id: order.id } })}>
              View order details
            </Button>
          </div>
        </div>
      </div>
    </Box>
  );
};

export default ListOrder;
